// @givmo/board — the recording officer's packet for one meeting.
//
// ══ A PACKET IS A SEQUENCE OF VERSIONS, AND THAT IS THE WHOLE MENTAL MODEL ══════════════════════
//
// One row per version. At most ONE draft per meeting, fenced by a partial unique index — two drafts are
// two people assembling different packets with no way to say which is next. Issuing a draft FREEZES it
// at the service and again at the database; a change opens the NEXT version. That is the record core's
// append-not-rewrite discipline applied to the artifact directors read, and it exists because a version
// whose contents can change is not a version — it moves the anchor under every note already written
// against it.
//
// Two consequences this page has to make visible rather than merely obey:
//
//   * AN ISSUED VERSION HAS NO EDIT AFFORDANCES AT ALL. Not disabled ones — absent. The officer's next
//     move is "start version N+1", and the screen says what that does before they do it.
//   * THE NEXT VERSION STARTS EMPTY. `create_packet` takes only the next version number; it copies
//     nothing. An officer who expects an editable duplicate and finds a blank one has lost their
//     assembly, so the confirmation says so in advance.
//
// ══ ORDER IS THE WHOLE LIST, NEVER ONE ROW ══════════════════════════════════════════════════════
//
// Reordering sends every entry id in the new order to `PUT …/item-order`, which renumbers inside one
// transaction. Its position constraint is DEFERRABLE INITIALLY DEFERRED precisely because two entries
// exchanging places are briefly equal under any ordering of two updates — so a single-row "set position
// 3" has no correct implementation here, and this page never sends one. Same shape as the agenda.
//
// ══ THE FINANCIAL PACK IS AN UPLOAD TODAY, AND THAT IS DELIBERATE ═══════════════════════════════
//
// `ledger_financial_pack_available` is the backend's detection seam and is false for every organization
// today — it flips per organization when the accounting module's ledger is live for them. Until it does, a
// treasurer's report reaches the board as an ATTACHMENT, which is why the attachments panel is a peer
// of the contents rather than an afterthought. Nothing here offers a generated section that does not
// exist yet.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '../../board-routing';

import { isGivmoError, type OrgBoardPacketOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  Icon,
  IconButton,
  Skeleton,
  Textarea,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { MeetingFrame } from '../meetings/meeting-frame';
import { useBoardHost, useBoardPermission } from '../../board-host';
import { AttachmentsPanel } from './attachments-panel';
import { DistributionPanel } from './distribution-panel';
import { PacketEntryDialog } from './packet-entry-dialog';
import { SharedCommentsPanel } from './shared-comments-panel';
import {
  displayInstant,
  draftVersion,
  extractPackets,
  itemKindLabel,
  itemTitle,
  packetStatusLabel,
  packetStatusTone,
  publishedVersions,
  sortPacketItems,
} from './packets-decode';
import { useBoardOperations } from '../../board-runtime';
import { meetingCommitteeName } from '../meetings/meetings-decode';

export function PacketPage() {
  const operations = useBoardOperations();
  const { meetingId } = useParams({ from: '/authenticated/meetings/$meetingId/packet' });
  const queryClient = useQueryClient();
  const { orgRef, isReady } = useBoardHost();
  const org = orgRef ?? '';
  // Starting, assembling, issuing and discarding a version are all `board_meeting:write`.
  const canWrite = useBoardPermission('board_meeting:write');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [startingNext, setStartingNext] = useState(false);
  const [attachmentsAvailable, setAttachmentsAvailable] = useState(true);

  const enabled = isReady && orgRef !== null;

  const meetingQuery = useQuery({
    ...operations.officerGetBoardMeetingOptions({
      path: { org_ref: org, meeting_id: meetingId },
    }),
    enabled,
  });

  const packetsOptions = operations.officerListBoardPacketsOptions({
    path: { org_ref: org, meeting_id: meetingId },
  });
  const packetsQuery = useQuery({ ...packetsOptions, select: extractPackets, enabled });
  const packets = packetsQuery.data ?? [];
  const draft = draftVersion(packets);
  const published = publishedVersions(packets);

  // Default to the draft if one exists, else the newest issued version. `packets` is already
  // newest-first, so `[0]` is the latest.
  const activeId = selectedId ?? draft?.id ?? packets[0]?.id ?? null;

  const packetOptions = operations.officerGetBoardPacketOptions({
    path: { org_ref: org, packet_id: activeId ?? '' },
  });
  const packetQuery = useQuery({ ...packetOptions, enabled: enabled && activeId !== null });
  const packet = packetQuery.data ?? null;
  const items = sortPacketItems(packet?.items ?? []);
  const isAssembling = packet?.status === 'draft';

  async function refreshPacket() {
    await queryClient.invalidateQueries({ queryKey: packetsOptions.queryKey });
    await queryClient.invalidateQueries({ queryKey: packetOptions.queryKey });
  }

  const reorder = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await operations.officerReorderBoardPacketItems({
        path: { org_ref: org, packet_id: activeId ?? '' },
        body: { packet_item_ids: orderedIds },
        throwOnError: true,
      });
    },
    onSuccess: () => void refreshPacket(),
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t reorder that. Please try again.',
      );
    },
  });

  const removeItem = useMutation({
    mutationFn: async (itemId: string) => {
      await operations.officerDeleteBoardPacketItem({
        path: { org_ref: org, packet_id: activeId ?? '', packet_item_id: itemId },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await refreshPacket();
      toast.success('Removed from the packet.');
    },
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t remove that. Please try again.',
      );
    },
  });

  const discard = useMutation({
    mutationFn: async () => {
      await operations.officerDiscardBoardPacket({
        path: { org_ref: org, packet_id: draft?.id ?? '' },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      setSelectedId(null);
      await refreshPacket();
      toast.success('Draft discarded.');
    },
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t discard that. Please try again.',
      );
    },
  });

  const startVersion = useMutation({
    mutationFn: async (): Promise<OrgBoardPacketOut> => {
      const { data } = await operations.officerCreateBoardPacket({
        path: { org_ref: org, meeting_id: meetingId },
        body: {},
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (created) => {
      setStartingNext(false);
      setSelectedId(created.id);
      await refreshPacket();
      toast.success(`Version ${created.version} is open for assembly.`);
    },
    onError: (caught) => {
      setStartingNext(false);
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t start a version. Please try again.',
      );
    },
  });

  /** Move one entry a single place and send the WHOLE resulting order (the renumber is transactional). */
  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const moved = next[index];
    const displaced = next[target];
    if (moved === undefined || displaced === undefined) return;
    next[index] = displaced;
    next[target] = moved;
    reorder.mutate(next.map((item) => item.id));
  }

  const meeting = meetingQuery.data ?? null;

  // The packet page is the meeting's "Packet" tab (CBG-UX1): once the meeting is read it wears the
  // meeting's shared title block and tabs, at this same address, so every earlier link to the packet
  // still opens it (a director's emailed packet link lands in the director tree instead,
  // `/board/director/packets/$packetId`). Until then (or if that read fails) the packet itself still renders under its own name,
  // inside the same frame, so the meeting's arrival never remounts it.
  const body = (
    <>
      {packetsQuery.isPending && enabled ? (
        <Skeleton className="h-84 w-full rounded-lg" testId="packet-loading" />
      ) : null}

      {packetsQuery.isError ? (
        <Card testId="packet-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              We couldn’t load this meeting’s packet.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void packetsQuery.refetch()}
              testId="packet-error-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {/* ── Versions ────────────────────────────────────────────────────────────────────────── */}
      {packetsQuery.isSuccess ? (
        <Card testId="packet-versions">
          <CardBody className="flex flex-col gap-16">
            <div className="flex flex-wrap items-center justify-between gap-12">
              <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                Versions
              </h2>
              {draft === null && canWrite ? (
                <Button
                  appearance="saas"
                  tone="primary"
                  size="sm"
                  loading={startVersion.isPending}
                  onClick={() =>
                    published.length > 0 ? setStartingNext(true) : startVersion.mutate()
                  }
                  testId="packet-start-version"
                >
                  {published.length > 0 ? 'Start the next version' : 'Start the packet'}
                </Button>
              ) : null}
            </div>

            {packets.length === 0 ? (
              <p
                className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp('packet-versions-empty')}
              >
                No packet has been started for this meeting yet.
              </p>
            ) : (
              <ul className="flex flex-col gap-8" data-testid="packet-version-list">
                {packets.map((version) => {
                  const active = version.id === activeId;
                  return (
                    <li key={version.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(version.id)}
                        aria-current={active ? 'true' : undefined}
                        className={[
                          'flex w-full flex-wrap items-center justify-between gap-10 rounded-lg border p-12 text-left outline-none transition-colors',
                          'focus-visible:shadow-ring',
                          active
                            ? 'border-brand-500 bg-surface-active'
                            : 'border-border-subtle hover:bg-surface-hover',
                        ].join(' ')}
                        data-testid={`packet-version-${version.version}`}
                      >
                        <span className="flex min-w-0 flex-col gap-2">
                          <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                            Version {version.version}
                          </span>
                          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                            {version.status === 'published'
                              ? `Issued ${displayInstant(version.published_at)}`
                              : 'Not issued yet'}
                          </span>
                        </span>
                        <Badge tone={packetStatusTone(version.status)}>
                          {packetStatusLabel(version.status)}
                        </Badge>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>
      ) : null}

      {/* ── Contents of the selected version ────────────────────────────────────────────────── */}
      {activeId !== null ? (
        <Card testId="packet-contents">
          <CardBody className="flex flex-col gap-16">
            <div className="flex flex-wrap items-center justify-between gap-12">
              <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                {packet !== null ? `What’s in version ${packet.version}` : 'Contents'}
              </h2>
              {isAssembling && canWrite ? (
                <div className="flex items-center gap-10">
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    onClick={() => setAdding(true)}
                    testId="packet-add-entry"
                  >
                    Add something
                  </Button>
                  <Button
                    appearance="saas"
                    tone="primary"
                    size="sm"
                    disabled={items.length === 0}
                    onClick={() => setPublishing(true)}
                    testId="packet-publish"
                  >
                    Issue this version
                  </Button>
                </div>
              ) : null}
            </div>

            {packetQuery.isPending ? (
              <Skeleton className="h-48 w-full rounded-lg" testId="packet-contents-loading" />
            ) : null}

            {packet !== null && !isAssembling ? (
              <p
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp('packet-frozen-note')}
              >
                Version {packet.version} was issued {displayInstant(packet.published_at)} and stays
                exactly as the board received it.
                {canWrite ? ' To change anything, start the next version.' : ''}
              </p>
            ) : null}

            {packetQuery.isSuccess && items.length === 0 ? (
              <p
                className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp('packet-contents-empty')}
              >
                {!isAssembling
                  ? 'This version went out with nothing in it.'
                  : canWrite
                    ? 'Nothing in this version yet. Add agenda items, files, records from the minute book, or a note of your own.'
                    : 'Nothing in this version yet.'}
              </p>
            ) : null}

            {items.length > 0 ? (
              <ol className="flex flex-col gap-8" data-testid="packet-item-list">
                {items.map((item, index) => (
                  <li
                    key={item.id}
                    className="flex items-start gap-12 rounded-lg border border-border-subtle p-12"
                    data-testid={`packet-item-${item.id}`}
                  >
                    <span className="mt-2 w-20 shrink-0 text-center font-sans text-s13 font-w600 tracking-givmo text-text-tertiary">
                      {index + 1}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-4">
                      <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                        {itemTitle(item)}
                      </span>
                      {item.body ? (
                        <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                          {item.body}
                        </span>
                      ) : null}
                      <span className="flex flex-wrap items-center gap-8">
                        <Badge tone="neutral">{itemKindLabel(item.item_kind)}</Badge>
                      </span>
                    </div>
                    {isAssembling && canWrite ? (
                      <div className="flex shrink-0 items-center gap-4">
                        <IconButton
                          aria-label={`Move “${itemTitle(item)}” up`}
                          tone="ghost"
                          size="sm"
                          disabled={index === 0 || reorder.isPending}
                          onClick={() => move(index, -1)}
                          testId={`packet-move-up-${item.id}`}
                        >
                          <Icon icon={ChevronUp} size={16} aria-hidden />
                        </IconButton>
                        <IconButton
                          aria-label={`Move “${itemTitle(item)}” down`}
                          tone="ghost"
                          size="sm"
                          disabled={index === items.length - 1 || reorder.isPending}
                          onClick={() => move(index, 1)}
                          testId={`packet-move-down-${item.id}`}
                        >
                          <Icon icon={ChevronDown} size={16} aria-hidden />
                        </IconButton>
                        <IconButton
                          aria-label={`Remove “${itemTitle(item)}” from the packet`}
                          tone="ghost"
                          size="sm"
                          disabled={removeItem.isPending}
                          onClick={() => removeItem.mutate(item.id)}
                          testId={`packet-remove-${item.id}`}
                        >
                          <Icon icon={Trash2} size={16} aria-hidden />
                        </IconButton>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ol>
            ) : null}

            {isAssembling && draft !== null && canWrite ? (
              <Button
                appearance="saas"
                tone="danger"
                size="sm"
                className="self-start"
                loading={discard.isPending}
                onClick={() => discard.mutate()}
                testId="packet-discard"
              >
                Discard this draft
              </Button>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {/* ── Files, sending, and the board's comments ────────────────────────────────────────── */}
      {enabled ? (
        <AttachmentsPanel
          orgRef={org}
          meetingId={meetingId}
          onStorageUnavailable={() => setAttachmentsAvailable(false)}
        />
      ) : null}

      {packet !== null && packet.status === 'published' ? (
        <>
          <DistributionPanel orgRef={org} packetId={packet.id} version={packet.version} />
          <SharedCommentsPanel
            orgRef={org}
            packetId={packet.id}
            committeeName={meetingCommitteeName(meeting)}
            committeeId={meeting?.committee_id ?? null}
          />
        </>
      ) : null}

      {adding && activeId !== null ? (
        <PacketEntryDialog
          orgRef={org}
          meetingId={meetingId}
          packetId={activeId}
          attachmentsAvailable={attachmentsAvailable}
          committeeName={meetingCommitteeName(meeting)}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            void refreshPacket();
          }}
        />
      ) : null}

      {publishing && activeId !== null ? (
        <PublishDialog
          orgRef={org}
          packetId={activeId}
          version={packet?.version ?? 0}
          entryCount={items.length}
          onClose={() => setPublishing(false)}
          onPublished={() => {
            setPublishing(false);
            void refreshPacket();
          }}
        />
      ) : null}

      {startingNext ? (
        <StartNextVersionDialog
          nextVersion={(packets[0]?.version ?? 0) + 1}
          pending={startVersion.isPending}
          onClose={() => setStartingNext(false)}
          onConfirm={() => startVersion.mutate()}
        />
      ) : null}
    </>
  );

  return (
    <MeetingFrame meeting={meeting} fallbackTitle="Board packet" tab="packet" testId="page-packet">
      {body}
    </MeetingFrame>
  );
}

/**
 * Issue a draft.
 *
 * The copy leads with what becomes irreversible, because it is: from here the version's contents are
 * frozen at the service AND at the database, and the only way to change anything is a new version.
 */
function PublishDialog({
  orgRef,
  packetId,
  version,
  entryCount,
  onClose,
  onPublished,
}: {
  orgRef: string;
  packetId: string;
  version: number;
  entryCount: number;
  onClose: () => void;
  onPublished: () => void;
}) {
  const operations = useBoardOperations();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const publish = useMutation({
    mutationFn: async () => {
      await operations.officerPublishBoardPacket({
        path: { org_ref: orgRef, packet_id: packetId },
        body: { note: note.trim().length > 0 ? note.trim() : null },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(`Version ${version} is issued.`);
      onPublished();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t issue that. Please try again.');
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !publish.isPending && onClose()}>
      <Dialog.Content
        aria-label="Issue this packet version"
        className="flex flex-col gap-16"
        testId="packet-publish-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Issue version {version}?
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          These {entryCount} {entryCount === 1 ? 'item' : 'items'} become version {version}, and its
          contents stop changing from that moment. If something needs to change afterwards, you
          start version {version + 1} — this one stays exactly as the board received it, so a note a
          director wrote against it still points at what they read.
        </p>

        <Textarea
          label="A line about this version (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          testId="packet-publish-note"
        />

        {error !== null ? <ErrorMessage testId="packet-publish-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={publish.isPending}
            onClick={onClose}
            testId="packet-publish-cancel"
          >
            Keep assembling
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={publish.isPending}
            onClick={() => {
              setError(null);
              publish.mutate();
            }}
            testId="packet-publish-confirm"
          >
            Issue it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * Confirm starting the next version.
 *
 * Its one job is the sentence about starting EMPTY. `create_packet` copies nothing, and an officer who
 * expects a duplicate they can trim finds a blank page instead — after the point where the previous
 * version can no longer be edited.
 */
function StartNextVersionDialog({
  nextVersion,
  pending,
  onClose,
  onConfirm,
}: {
  nextVersion: number;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onClose()}>
      <Dialog.Content
        aria-label="Start the next packet version"
        className="flex flex-col gap-16"
        testId="packet-next-version-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Start version {nextVersion}?
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          Version {nextVersion} starts empty — nothing is copied across, so you’ll add its contents
          again. Everything already issued stays exactly as the board received it, and files
          attached to this meeting are still there to reuse.
        </p>

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={pending}
            onClick={onClose}
            testId="packet-next-version-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={pending}
            onClick={onConfirm}
            testId="packet-next-version-confirm"
          >
            Start version {nextVersion}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
